// src/hooks/useCarModels.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCarModelsPaginated,
  fetchCarModelStats,
  createCarModel,
  updateCarModel,
  deleteCarModel,
  toggleCarModelStatus,
} from '../admin/pages/carmodel/carModelService';
import { CarModelFormData } from '../admin/pages/carmodel/carModel';

export const CAR_MODEL_KEYS = {
  all:       ['carModels'] as const,
  lists:     () => ['carModels', 'list'] as const,
  paginated: (page: number, search: string) =>
               ['carModels', 'list', page, search] as const,
  stats:     () => ['carModels', 'stats'] as const,
};

export interface CarModelPaginationParams {
  page: number;
  search: string;
  pageSize: number;
}

export const useCarModelsPaginated = (params: CarModelPaginationParams) => {
  return useQuery({
    queryKey: CAR_MODEL_KEYS.paginated(params.page, params.search),
    queryFn: () =>
      fetchCarModelsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useCarModelStats = () => {
  return useQuery({
    queryKey: CAR_MODEL_KEYS.stats(),
    queryFn: fetchCarModelStats,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCarModelMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: CAR_MODEL_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({ formData, userId }: { formData: CarModelFormData; userId: string }) =>
      createCarModel(formData, userId),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({ carModelId, formData }: { carModelId: string; formData: CarModelFormData }) =>
      updateCarModel(carModelId, formData),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (carModelId: string) => deleteCarModel(carModelId),
    onSuccess: invalidateAll,
  });

  const toggle = useMutation({
    mutationFn: ({ carModelId, isActive }: { carModelId: string; isActive: boolean }) =>
      toggleCarModelStatus(carModelId, isActive),
    onSuccess: invalidateAll,
  });

  return { create, update, remove, toggle };
};
